<template>
  <div class="party-form">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('party') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('kind')"
          label-for="kind"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('kind') + ' ' + $t('select')"
            v-model="localParty.kind"
            :options="getEnumValues('PartyKind')"
            :show-labels="true"
            :allow-empty="false"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option ? $t(option) : '' }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('eligibleFor')"
          label-for="eligibleFor"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('eligibleFor') + ' ' + $t('select')"
            v-model="localParty.eligibleFor"
            :options="getEnumValues('PartyEligibleFor')"
            :multiple="true"
            :show-labels="true"
            :close-on-select="false"
            :taggable="false"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
            <template slot="tag" slot-scope="{ option }">
              <div>{{ $t(option) }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('company')"
          label-for="company"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.company" type="text" :placeholder="$t('company')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('salutation')"
          label-for="salutation"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            v-model="localParty.salutation"
            id="salutation"
            :options="getEnumValues('Salutation')"
            :placeholder="$t('select_salutation')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option ? $t(option) : '' }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('firstname')"
          label-for="firstname"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.firstname" type="text" :placeholder="$t('firstname')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('lastname')"
          label-for="lastname"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.lastname" type="text" :placeholder="$t('lastname')" />
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('address') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('street')"
          label-for="street"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.street" type="text" :placeholder="$t('street')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('streetNumber')"
          label-for="streetNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="localParty.streetNumber"
            type="text"
            :placeholder="$t('streetNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('streetNumberSuffix')"
          label-for="streetNumberSuffix"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="localParty.streetNumberSuffix"
            type="text"
            :placeholder="$t('streetNumberSuffixShort')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('zip')"
          label-for="zip"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.zip" type="text" :placeholder="$t('zip')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('city')"
          label-for="city"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.city" type="text" :placeholder="$t('city')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('cityPart')"
          label-for="cityPart"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.cityPart" type="text" :placeholder="$t('cityPart')" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('contact') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('phone')"
          label-for="phone"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.phone" type="text" :placeholder="$t('phone')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('phone2')"
          label-for="phone2"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.phone2" type="text" :placeholder="$t('phone2')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('phone3')"
          label-for="phone3"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.phone3" type="text" :placeholder="$t('phone3')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('mobile')"
          label-for="mobile"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.mobile" type="text" :placeholder="$t('mobile')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('mobile2')"
          label-for="mobile2"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.mobile2" type="text" :placeholder="$t('mobile2')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('email')"
          label-for="email"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.email" type="email" :placeholder="$t('email')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('email2')"
          label-for="email2"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.email2" type="email" :placeholder="$t('email2')" />
        </b-form-group>

        <div class="attachments-section mt-4">
          <legend>
            <strong>{{ $t('attachments') }}</strong>
          </legend>

          <div v-if="localParty._id && localParty._id !== '_new'">
            <b-form-group
              horizontal
              :label="$t('attachmentType')"
              label-for="attachmentType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('attachmentType')"
                v-model="selectedAttachmentType"
                id="attachmentType"
                :options="attachmentTypes"
                :allow-empty="false"
                :show-labels="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>

              <DropzoneUpload
                class="mt-2"
                :complete="isAttachmentUploadComplete"
                :parentId="localParty._id"
                :parentType="selectedAttachmentType"
                :uploadCallback="uploadPartyAttachment"
                :disabled="isUploadDisabled"
                v-on:dropzone-upload:reset="isAttachmentUploadComplete = false"
              />
            </b-form-group>
          </div>

          <p v-else class="text-muted">
            {{ $t('save_before_upload') }}
          </p>

          <div v-for="attachmentType in attachmentTypes" :key="attachmentType">
            <legend v-if="getAttachmentsByType(attachmentType).length > 0">
              <strong>{{ $t(attachmentType) }}</strong>
            </legend>
            <AttachmentList
              :attachments="getAttachmentsByType(attachmentType)"
              :attachmentType="attachmentType"
              v-on:attachment-list:delete="onDeleteAttachment"
            />
          </div>
        </div>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('MISC') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('birthdayAt')"
          label-for="birthdayAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="birthdayAtDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="1920:2030"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('taxNumber')"
          label-for="taxNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.taxNumber" type="text" :placeholder="$t('taxNumber')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('vatId')"
          label-for="vatId"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.vatId" type="text" :placeholder="$t('vatId')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('bank')"
          label-for="bank"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.bank" type="text" :placeholder="$t('bank')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('iban')"
          label-for="iban"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.iban" type="text" :placeholder="$t('iban')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('bic')"
          label-for="bic"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input v-model="localParty.bic" type="text" :placeholder="$t('bic')" />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('accountHolder')"
          label-for="accountHolder"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="localParty.accountHolder"
            type="text"
            :placeholder="$t('accountHolder')"
          />
        </b-form-group>

        <legend>
          <strong>{{ $t('additionalInfo') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('notes')"
          label-for="notes"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-textarea v-model="localParty.notes" :placeholder="$t('notes')" rows="3" />
        </b-form-group>

        <b-form-group
          v-if="localParty.number && localParty.number !== '_new'"
          horizontal
          :label="$t('number')"
          label-for="number"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localParty.number.toString().padStart(4, '0') }}
          </div>
        </b-form-group>

        <b-form-group
          v-if="localParty.createdBy"
          horizontal
          :label="$t('createdBy')"
          label-for="createdBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localParty.createdBy.firstname }} {{ localParty.createdBy.lastname
            }}<span v-if="localParty.createdAt"
              >, {{ $dayjs(localParty.createdAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>

        <b-form-group
          v-if="localParty.updatedBy"
          horizontal
          :label="$t('updatedBy')"
          label-for="updatedBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localParty.updatedBy.firstname }} {{ localParty.updatedBy.lastname
            }}<span v-if="localParty.updatedAt"
              >, {{ $dayjs(localParty.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import { mapGetters, mapActions } from 'vuex';
import AttachmentList from '@/components/AttachmentList.vue';
import DropzoneUpload from '@/components/DropzoneUpload.vue';

export default {
  name: 'PartyForm',
  components: {
    Multiselect,
    Calendar,
    AttachmentList,
    DropzoneUpload,
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localParty: { ...this.party },
      isAttachmentUploadComplete: false,
      selectedAttachmentType: 'PARTY_DOCUMENT',
    };
  },
  computed: {
    ...mapGetters(['getEnumValues']),

    birthdayAtDate: {
      get() {
        return this.localParty.birthdayAt ? this.$dayjs(this.localParty.birthdayAt).toDate() : null;
      },
      set(value) {
        this.localParty.birthdayAt = value;
        this.emitUpdate();
      },
    },
    attachmentTypes() {
      return ['PARTY_DOCUMENT'];
    },
    isUploadDisabled() {
      return this.selectedAttachmentType === null;
    },
  },
  watch: {
    party: {
      handler(newParty) {
        this.localParty = { ...newParty };
      },
      deep: true,
      immediate: true,
    },
    localParty: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues', 'uploadPartyAttachment', 'deletePartyAttachment']),

    emitUpdate() {
      this.$emit('update:party', this.localParty);
    },

    handleSave() {
      this.$emit('save', this.localParty);
    },
    async onDeleteAttachment(event) {
      await this.deletePartyAttachment({
        partyId: this.localParty._id,
        attachmentId: event.attachmentId,
      });
    },
    getAttachmentsByType(attachmentType) {
      if (!this.localParty.attachments) return [];
      return this.localParty.attachments.filter(
        (attachment) => attachment.attachmentType === attachmentType
      );
    },
  },
  async mounted() {
    await this.fetchEnumValues('PartyKind');
    await this.fetchEnumValues('PartyEligibleFor');
    await this.fetchEnumValues('Salutation');

    this.$store.subscribeAction({
      after: (action) => {
        if (action.type === 'uploadPartyAttachment') {
          this.isAttachmentUploadComplete = true;
        }
      },
    });
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}
</style>
